import type { PillProps } from '@cwos/ui-admin';
import type {
  AgentActionResult,
  AgentDeploymentStatus,
  AgentRunStatus,
  AgentSummary,
  FireTimeAuthorityVerdict,
  FireTimeKeyStanding,
} from './agentsClient.js';

/**
 * The vocabulary layer for Intelligence → Agents.
 *
 * Pure, so every sentence on the screen is assertable without mounting it.
 * Three rules run through all of it:
 *
 *  1. NO STORAGE WORDS ON THE SCREEN. `POLICY_BLOCKED`, `OUTCOME_UNKNOWN` and
 *     `deployer_live_holds` are CHECK-constraint and union vocabulary. An
 *     operator reading them is being shown the schema and told it is a
 *     product. Every one of them is translated here, once.
 *
 *  2. A CONFIGURED CAPABILITY IS NOT AN EFFECTIVE ONE. Nothing in this file
 *     turns `permissionProfile` into a sentence about what the agent may do.
 *     The only functions that make that claim take a
 *     `FireTimeAuthorityVerdict`, because that is the only input that knows.
 *
 *  3. FOUR STATES, NEVER THREE. Withheld, absent, a measured zero and
 *     refused-for-scope get four different sentences wherever they can all
 *     occur — most sharply in `activitySentence`, where "nothing has been
 *     recorded" and "this is the newest page of a longer history" are the
 *     difference between an audit surface and a decoration.
 */

// ---------------------------------------------------------------------------
// Deployment status
// ---------------------------------------------------------------------------

/**
 * The status a customer sees, in their words.
 *
 * DRAFT/CONFIGURED/DEPLOYED are three internal steps of one thing — an agent
 * that exists and has not been switched on — and a customer has no verb that
 * distinguishes them, so they share a label. ACTIVE, PAUSED and DISABLED are
 * genuinely different situations with different remedies, so they do not.
 */
export function statusLabel(status: AgentDeploymentStatus): string {
  switch (status) {
    case 'ACTIVE':
      return 'Running';
    case 'PAUSED':
      return 'Paused';
    case 'DISABLED':
      return 'Stopped';
    case 'DRAFT':
    case 'CONFIGURED':
    case 'DEPLOYED':
      return 'Not started';
  }
}

export function statusPill(status: AgentDeploymentStatus): PillProps['variant'] {
  switch (status) {
    case 'ACTIVE':
      return 'good';
    case 'PAUSED':
      return 'look';
    case 'DISABLED':
      return 'plain';
    default:
      return 'plain';
  }
}

/**
 * The kill switch is TWO fields — `status` and `enabled` — and a run needs
 * both. A screen that reads only the status will call an agent Running while
 * every fire is refused at the kill-switch check.
 */
export function isLive(agent: Pick<AgentSummary, 'status' | 'enabled'>): boolean {
  return agent.status === 'ACTIVE' && agent.enabled;
}

// ---------------------------------------------------------------------------
// Run status
// ---------------------------------------------------------------------------

export function runStatusLabel(status: AgentRunStatus): string {
  switch (status) {
    case 'QUEUED':
      return 'Waiting to start';
    case 'PLANNING':
      return 'Working out what to do';
    case 'EXECUTING':
      return 'Doing it';
    case 'WAITING_FOR_APPROVAL':
      return 'Waiting for someone to approve';
    case 'WAITING_FOR_EVENT':
      return 'Waiting for something to happen';
    case 'COMPLETED':
      return 'Finished';
    case 'FAILED':
      return 'Failed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'BUDGET_EXHAUSTED':
      return 'Stopped — out of budget';
    case 'POLICY_BLOCKED':
      return 'Refused — not permitted at the time';
  }
}

export function runStatusPill(status: AgentRunStatus): PillProps['variant'] {
  switch (status) {
    case 'COMPLETED':
      return 'good';
    case 'FAILED':
    case 'POLICY_BLOCKED':
      return 'urgent';
    case 'BUDGET_EXHAUSTED':
    case 'WAITING_FOR_APPROVAL':
      return 'look';
    default:
      return 'plain';
  }
}

const LIVE_RUN_STATUSES: readonly AgentRunStatus[] = [
  'QUEUED',
  'PLANNING',
  'EXECUTING',
  'WAITING_FOR_APPROVAL',
  'WAITING_FOR_EVENT',
];

export function isRunLive(status: AgentRunStatus): boolean {
  return LIVE_RUN_STATUSES.includes(status);
}

// ---------------------------------------------------------------------------
// What an action actually did
// ---------------------------------------------------------------------------

/**
 * The six outcomes, and the three at the end are the whole point.
 *
 * FAILED is a measured negative: the executor answered with an error and the
 * effect did not happen. OUTCOME_UNKNOWN is the fourth state — the reaper
 * found an in-flight row whose run was abandoned, so nobody watched how the
 * attempt ended and the change may or may not be sitting in the records right
 * now. `null` is not an outcome at all; nothing was attempted yet.
 *
 * Collapsing OUTCOME_UNKNOWN into either neighbour is a claim this system is
 * not entitled to make, and it is exactly the claim a customer would act on.
 */
export function actionOutcomeSentence(result: AgentActionResult): string {
  switch (result) {
    case 'EXECUTED':
      return 'Done';
    case 'SKIPPED_DUPLICATE':
      return 'Skipped — already done';
    case 'REFUSED':
      return 'Refused before it ran';
    case 'FAILED':
      return 'Attempted and failed — nothing changed';
    case 'IN_FLIGHT':
      return 'Attempted — still in progress';
    case 'OUTCOME_UNKNOWN':
      return 'Attempted, outcome never observed';
    case null:
      return 'Not attempted';
  }
}

/** The longer sentence, for the one outcome that needs explaining. */
export function actionOutcomeNote(result: AgentActionResult): string | null {
  if (result === 'OUTCOME_UNKNOWN') {
    return (
      'The run stopped before anyone saw how this ended. It is not a failure and it is not a ' +
      'success: the change may or may not have been made, and this record says so rather than ' +
      'guessing.'
    );
  }
  if (result === 'FAILED') {
    return 'The attempt returned an error, so nothing was changed.';
  }
  return null;
}

export function actionOutcomePill(result: AgentActionResult): PillProps['variant'] {
  switch (result) {
    case 'EXECUTED':
      return 'good';
    case 'FAILED':
      return 'urgent';
    case 'OUTCOME_UNKNOWN':
      return 'look';
    default:
      return 'plain';
  }
}

// ---------------------------------------------------------------------------
// Fire-time authority — the substance of this screen
// ---------------------------------------------------------------------------

/**
 * The heading sentence for the authority panel.
 *
 * It is always in the FUTURE CONDITIONAL — "would" — and never in the present
 * indicative, because the verdict is about the next fire and not about now.
 * "This agent can create tasks" is a sentence about a setting. "If it ran now,
 * it would be allowed to act" is a sentence about an evaluation, and it is the
 * only one this data supports.
 */
export function authorityHeadline(verdict: FireTimeAuthorityVerdict): string {
  if (verdict.allowed) return 'If this agent ran right now, it would be allowed to act.';
  return 'If this agent ran right now, it would be refused.';
}

/** Why it would be refused, in the reader's terms. `null` when it would not. */
export function authorityReason(verdict: FireTimeAuthorityVerdict): string | null {
  switch (verdict.refusedAt) {
    case null:
      return null;
    case 'kill_switch':
      return 'It is not switched on. A paused or stopped agent is refused before anything else is looked at.';
    case 'empty_profile':
      return 'It was delegated no permissions at all, so there is nothing it could be allowed to do.';
    case 'pilot_enrolment':
      return 'Your organization is not currently enrolled for agent execution. That enrolment is checked again every time an agent fires, so this is a live answer and not a configuration.';
    case 'deployer_inactive':
      return 'The person who deployed it no longer has an active account here. An agent acts with the authority of the person who deployed it, and there is no longer an account to borrow it from.';
    case 'never_delegable':
      return 'One of the permissions it was delegated can never be given to an agent, whoever holds it.';
    case 'unknown_key':
      return 'One of the permissions it was delegated is not a permission this product defines. Unknown means refused.';
    case 'not_held':
      return 'The person who deployed it no longer holds every permission it was given. One missing permission refuses the whole run — an agent acting with part of what was delegated is not acting with what was delegated.';
    case 'identity':
      return 'The run did not match the agent it claimed to be.';
  }
}

/**
 * What the authority is actually derived FROM, said plainly.
 *
 * The two branches are different systems, and a surface that described them
 * identically would be claiming a live catalog re-derivation for a deployment
 * that has no tenant deployer to re-derive from.
 */
export function authoritySourceSentence(verdict: FireTimeAuthorityVerdict): string {
  if (verdict.authoritySource === 'platform_enrolment') {
    return (
      'This agent was deployed by Phibian’s operations team, not by anyone in your organization. ' +
      'There is no member of your organization whose permissions it borrows, so there is nothing ' +
      'to re-check against your permission settings. What IS re-checked, every time it fires, is ' +
      'whether your organization is enrolled for agent execution: ' +
      (verdict.pilotEnrolled ? 'it is.' : 'it is not.')
    );
  }
  return (
    'This agent acts with the permissions of the person who deployed it. Those permissions are ' +
    'read again from your organization’s live settings every time it fires, and again before ' +
    'every single action it takes — not stored on the agent when it was set up.'
  );
}

/** The deployer's standing, for the tenant branch. `null` for a platform agent. */
export function deployerSentence(verdict: FireTimeAuthorityVerdict): string | null {
  switch (verdict.deployer) {
    case 'not_a_tenant_user':
      return null;
    case 'active':
      return 'The account it borrows from is active.';
    case 'inactive':
      return 'The account it borrows from is no longer active, so every run is refused.';
    case 'absent':
      return 'The account it borrows from could not be read, so every run is refused.';
  }
}

/**
 * One delegated key, in the customer's words.
 *
 * `not_evaluated` gets its own sentence and never the absence of one. It means
 * the derivation refused before this key was reached — there was no active
 * deployer to read holds from — so nobody looked, and a blank cell or a
 * crossed-out row would both be reporting a measurement that was never taken.
 */
export function keyStandingLabel(standing: FireTimeKeyStanding): string {
  switch (standing) {
    case 'would_allow':
      return 'Would be allowed';
    case 'never_delegable':
      return 'Never allowed for an agent';
    case 'not_in_catalog':
      return 'Not a permission this product has';
    case 'not_held_by_deployer':
      return 'No longer held by the deployer';
    case 'not_evaluated':
      return 'Not checked';
  }
}

export function keyStandingNote(standing: FireTimeKeyStanding): string | null {
  if (standing === 'not_evaluated') {
    return 'Nothing was refused about this permission and nothing was allowed — the check stopped before reaching it, so its standing is genuinely unknown.';
  }
  return null;
}

export function keyStandingPill(standing: FireTimeKeyStanding): PillProps['variant'] {
  switch (standing) {
    case 'would_allow':
      return 'good';
    case 'never_delegable':
    case 'not_in_catalog':
    case 'not_held_by_deployer':
      return 'urgent';
    case 'not_evaluated':
      return 'plain';
  }
}

/**
 * The caveat that must accompany every authority panel.
 *
 * Not decoration and not hedging: without it a reader takes the panel for a
 * settings screen, and the single most important property of this data — that
 * it is re-derived, that it can change without anyone touching the agent — is
 * invisible.
 */
export function authorityFreshnessSentence(evaluatedAt: number, now: number): string {
  const secondsAgo = Math.max(0, Math.round((now - evaluatedAt) / 1000));
  const when =
    secondsAgo < 60
      ? 'a moment ago'
      : secondsAgo < 3600
        ? `${Math.round(secondsAgo / 60)} minutes ago`
        : 'earlier';
  return (
    `Worked out ${when}, not saved on the agent. This is not a setting you are looking at: if ` +
    'someone changes a permission in the next minute, the next run uses the new answer and this ' +
    'page will show it after a refresh.'
  );
}

// ---------------------------------------------------------------------------
// Run history and the audit trail
// ---------------------------------------------------------------------------

/**
 * The last-run cell.
 *
 * `null` is a MEASURED ABSENCE — the runs read succeeded and there were none.
 * It is not "we did not look", and it is not "you may not see them", both of
 * which reach the screen through other paths and say so in other words.
 */
export function lastRunSentence(agent: Pick<AgentSummary, 'lastRun'>): string {
  if (agent.lastRun === null) return 'Has never run';
  const run = agent.lastRun;
  if (run.startedAt === null) return `${runStatusLabel(run.status)} — not started yet`;
  return runStatusLabel(run.status);
}

/**
 * What the agent's audit trail says about its own completeness.
 *
 * FOUR ANSWERS, and the first two are the pair this codebase keeps collapsing:
 *
 *   refused   the reader may not have it. Never rendered as a count.
 *   empty     the deployment-scoped query ran and matched nothing. A real
 *             zero, and only sayable because the query is scoped in SQL —
 *             the platform door's in-memory filter over the newest 500 rows
 *             cannot distinguish this from "the window did not reach back".
 *   complete  every row this agent ever wrote is on the page.
 *   partial   the newest page of a longer history.
 */
export function activitySentence(input: { count: number; hasMore: boolean }): string {
  if (input.count === 0) {
    return 'Nothing has been recorded for this agent yet. This is the agent’s own audit trail, searched in full — not a window that might have missed it.';
  }
  return input.hasMore
    ? 'These are the most recent entries. There are older ones beyond this page.'
    : 'That is this agent’s complete recorded history.';
}

/**
 * The canonical audit verb, in the customer's words.
 *
 * The `agents.*` vocabulary is closed (27 verbs) and this covers it. An
 * unrecognised verb falls back to the raw action rather than to a guess: a
 * new verb appearing on screen as itself is ugly and truthful, and mapping it
 * to "something happened" would be neither.
 */
export function auditVerbSentence(action: string): string {
  const known: Record<string, string> = {
    'agents.deployment.created': 'Agent created',
    'agents.deployment.activated': 'Agent switched on',
    'agents.deployment.paused': 'Agent paused',
    'agents.deployment.disabled': 'Agent stopped',
    'agents.deployment.updated': 'Agent changed',
    'agents.deployment.emergency_stopped': 'Emergency stop',
    'agents.run.started': 'Run started',
    'agents.run.completed': 'Run finished',
    'agents.run.failed': 'Run failed',
    'agents.run.cancelled': 'Run cancelled',
    'agents.run.policy_blocked': 'Run refused — not permitted',
    'agents.run.waiting': 'Run paused, waiting',
    'agents.run.resumed': 'Run resumed',
    'agents.run.emergency_stop': 'Run emergency-stopped',
    'agents.plan.accepted': 'Plan accepted',
    'agents.plan.rejected': 'Plan rejected',
    'agents.action.prepared': 'Action prepared',
    'agents.action.executed': 'Action carried out',
    'agents.action.policy_blocked': 'Action refused — not permitted',
    'agents.action.failed': 'Action failed',
    'agents.action.retried': 'Action retried',
    'agents.approval.requested': 'Approval requested',
    'agents.approval.granted': 'Approval granted',
    'agents.approval.denied': 'Approval denied',
    'agents.authority.refused': 'Refused — permission had been taken away',
    'agents.budget.exhausted': 'Stopped — out of budget',
    'agents.outcome.recorded': 'Outcome recorded',
  };
  return known[action] ?? action;
}

/**
 * Who acted, three-valued.
 *
 * `null` on an agent audit row does NOT mean nobody: the canonical sink drops
 * an actor the tenant does not host rather than fabricate one, so a platform
 * operator's row arrives with a null actor and names its operator in the
 * detail instead. Rendering that as "the system" would quietly attribute a
 * vendor action to the software.
 */
export function auditActorSentence(entry: {
  actorUserId: string | null;
  detail: Record<string, unknown>;
}): string {
  if (entry.actorUserId !== null) return 'Someone in your organization';
  return typeof entry.detail.operatorId === 'string' ? 'Phibian operations' : 'The agent itself';
}

// ---------------------------------------------------------------------------
// Triggers
// ---------------------------------------------------------------------------

/**
 * What makes it run, said honestly about what the runtime can actually do.
 *
 * SCHEDULE and MANUAL have dispatchers. EVENT has a queue with no producer;
 * THRESHOLD and WEBHOOK have no dispatcher at all. A deployment carrying one
 * of the last three is a configured capability with a silent zero behind it,
 * and this surface is the only place a customer would ever find that out.
 */
export function triggerSentence(trigger: { kind: string; cron?: string }): string {
  switch (trigger.kind) {
    case 'MANUAL':
      return 'Only when someone starts it';
    case 'SCHEDULE':
      return trigger.cron ? `On a schedule (${trigger.cron})` : 'On a schedule';
    case 'EVENT':
      return 'When something happens — nothing raises this yet, so it will not fire';
    case 'THRESHOLD':
      return 'When a measure crosses a limit — not yet built, so it will not fire';
    case 'WEBHOOK':
      return 'When an outside system calls in — not yet built, so it will not fire';
    default:
      return trigger.kind;
  }
}

/** True for the trigger kinds no dispatcher serves. */
export function triggerIsInert(kind: string): boolean {
  return kind === 'EVENT' || kind === 'THRESHOLD' || kind === 'WEBHOOK';
}
